import React, { useState, useEffect } from "react";
import { CircularProgress } from "@material-ui/core";
import { useTranslation } from "react-i18next";

import { extendWithLoadingAndFail } from "App/Modules/Services";
import type { IEquipmentRepository } from "App/Modules/Repositories";
import type { IEquipmentSlim, IResponseWithLoading } from "App/Modules/Models";

import { SearchField, jsonEqual } from "App/ReactComponents/Shared";
import "./_equipment.scss";

type InjectedProps = {
    mmEquipmentRepository: IEquipmentRepository;
};

type EquipmentProps = InjectedProps;

export const Equipment = (props: EquipmentProps) => {
    const [globalFilter, setGlobalFilter] = useState("");
    const [loadingEquipment, setLoadingEquipment] = useState(true);
    const [equipment, setEquipment] = useState<IResponseWithLoading<IEquipmentSlim[]>>();
    useEffect(() => {
        if (!equipment) {
            extendWithLoadingAndFail(props.mmEquipmentRepository.getEquipmentList()).subscribe(
                (response) => {
                    if (!response.isLoading && !jsonEqual(response, equipment)) {
                        setEquipment(response);
                        setLoadingEquipment(false);
                    }
                },
                (error) => {
                    setEquipment(error);
                    setLoadingEquipment(false);
                },
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { t } = useTranslation();
    const filterFunc = (eq: IEquipmentSlim) => {
        if (!globalFilter) {
            return true;
        }
        return (
            eq.type.manufacturer.name.toLowerCase().indexOf(globalFilter.toLowerCase()) > -1 ||
            eq.type.model.toLowerCase().indexOf(globalFilter.toLowerCase()) > -1 ||
            eq.serialNumber.toLowerCase().indexOf(globalFilter.toLowerCase()) > -1 ||
            eq.partner.name.toLowerCase().indexOf(globalFilter.toLowerCase()) > -1
        );
    };

    const filteredRows = () => equipment?.data?.filter((eq) => filterFunc(eq));

    return (
        <div className="card equipment">
            <h1 className="title title--h2">{t("Entities.Equipment.EquipmentList")}</h1>
            <div className="equipment__functions">
                <div className="equipment__filter">
                    <SearchField globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
                </div>
            </div>
            {equipment && equipment.data?.length > 0 ? (
                <div className="caption">
                    {t("Entities.Equipment.EquipmentsShown", {
                        filteredEquipment: filteredRows().length,
                        totalEquipment: equipment.data.length,
                    })}
                </div>
            ) : null}
            <div className="scrollable-list card card--nested card--alt">
                <div className="scrollable-list__scroller scrollable-list__scroller--large">
                    <div className="entity-list">
                        {loadingEquipment ? (
                            <div className="entity-list__item entity-list__item--loading">
                                <CircularProgress />
                            </div>
                        ) : null}
                        {filteredRows()?.map((eq) => (
                            <div className="entity-list__item" key={eq.id}>
                                <div className="entity-list__item-content">
                                    <div className="entity-list__item-icon entity-list__item-icon--alt">
                                        <i className={`fal fa-${eq.type.icon}`} />
                                    </div>
                                    <div className="entity-list__item-panel">
                                        <div className="paragraph paragraph--no-spacing">
                                            {`${eq.type.manufacturer.name}, ${eq.type.model}`}
                                        </div>
                                        <div className="caption caption--no-spacing">
                                            {eq.serialNumber}
                                            {eq.originalSerialNumber ? `, ${eq.originalSerialNumber}` : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
